import { gql, useLazyQuery } from "@apollo/client";
import { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  Button,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaCog, FaFileCsv } from "react-icons/fa";
import { useUserList } from "../../contexts/UserListContext";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import useApolloListData from "../../useApolloListData";

const gqlQuery = gql`
  query partList($service_part_list_request: service_part_list_request) {
    service_part(service_part_list_request: $service_part_list_request) {
      edges {
        id
        part_no
        quantity
        created_at
        ticket_id
        service_id
        crane_id
        user_id
        crane {
          id
          name
        }
        ticket {
          id
          name
        }
        service {
          id
          service_definition {
            name
          }
        }
      }
      totalCount
    }
  }
`;

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  partNo: "partNo",
  craneId: "craneId",
  sourceType: "sourceType",
};

function partSourceType(part) {
  if (part.ticket_id) return "Ticket";
  if (part.service_id) return "Service";
  return "—";
}

function partSourceLabel(part) {
  if (part.ticket_id) {
    return part.ticket?.name ? `Ticket #${part.ticket_id} — ${part.ticket.name}` : `Ticket #${part.ticket_id}`;
  }
  if (part.service_id) {
    const defName = part.service?.service_definition?.name;
    return defName ? `Service #${part.service_id} — ${defName}` : `Service #${part.service_id}`;
  }
  return "—";
}

export default function PartList({ id: entityId, action, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const userList = useUserList();

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, partNo, craneId, sourceType } = requestState;
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionPartNo, setOptionPartNo] = useState(partNo);
  const [optionCraneId, setOptionCraneId] = useState(craneId);
  const [optionSourceType, setOptionSourceType] = useState(sourceType || "");
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      partNo: optionPartNo,
      craneId: optionCraneId,
      sourceType: optionSourceType || null,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionPartNo("");
    setOptionCraneId(null);
    setOptionSourceType("");
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "Part ID", sortBy: "id" },
    { text: "Part Number", sortBy: "part_no" },
    { text: "Quantity", sortBy: "quantity" },
  ]);
  const sortDirectionOptions = [
    { text: "A to Z", sortDirection: "ASC" },
    { text: "Z to A", sortDirection: "DESC" },
  ];

  let variables = {
    service_part_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
  };

  if (sortBy && sortDirection) {
    variables.service_part_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
    });
  } else {
    variables.service_part_list_request.sorting.push({
      field: "created_at",
      direction: "DESC",
    });
  }

  if (partNo) {
    variables.service_part_list_request.filters.push({
      field: "part_no",
      operator: "like",
      value: `%${partNo}%`,
    });
  }
  if (craneId) {
    variables.service_part_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(craneId),
    });
  }
  if (sourceType === "ticket") {
    variables.service_part_list_request.filters.push({
      field: "ticket_id",
      operator: ">",
      value: "0",
    });
  } else if (sourceType === "service") {
    variables.service_part_list_request.filters.push({
      field: "service_id",
      operator: ">",
      value: "0",
    });
  }

  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 10000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });
  const list = useApolloListData(data, (localData) => localData?.service_part?.edges);

  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
  }, [requestState, limit]);

  const count = data?.service_part?.totalCount ?? 0;

  if (props.isMaster && props.autoSelect && !loading && !entityId && list.length) {
    props.onSelect(list[0].id, false);
  }

  const formatDate = (iso) => {
    if (!iso) return "—";
    const dt = DateTime.fromISO(iso, { zone: "utc" });
    return dt.isValid ? dt.toLocal().toFormat("MMM d, yyyy") : "—";
  };

  const techName = (userId) => {
    const found = userList.find((u) => String(u._id) === String(userId));
    return found?.name ?? found?.username ?? "—";
  };

  return (
    <OptionsMenu isOpen={ isFilterMenuOpen }>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button onClick={ () => { applyOptions(); setIsFilterMenuOpen(false); } } loading={ loading }>
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>
          <div className="option">
            <label>Part Number:</label>
            <input value={ optionPartNo } onChange={ (e) => setOptionPartNo(e.target.value) } />
          </div>
          <div className="option">
            <label>Source:</label>
            <select value={ optionSourceType } onChange={ (e) => setOptionSourceType(e.target.value) }>
              <option value="">All</option>
              <option value="ticket">Ticket</option>
              <option value="service">Service</option>
            </select>
          </div>
          <div className="option">
            <label>Crane:</label>
            <CraneDropDown
              selected={ optionCraneId }
              onChange={ setOptionCraneId }
              searchEnabled
              multiple={ false }
            />
          </div>
        </div>

        <div className="options-section">
          <h3>Sorting</h3>
          <div className="option">
            <label>Sort By:</label>
            <select value={ optionSortBy } onChange={ (e) => setOptionSortBy(e.target.value) }>
              { sortByOptions.map((option) => (
                <option key={ option.text } value={ option.sortBy }>{ option.text }</option>
              )) }
            </select>
          </div>
          <div className="option">
            <label>Sort Order:</label>
            <select value={ optionSortDirection } onChange={ (e) => setOptionSortDirection(e.target.value) }>
              { sortDirectionOptions.map((option) => (
                <option key={ option.text } value={ option.sortDirection }>{ option.text }</option>
              )) }
            </select>
          </div>
        </div>

        <div className="options-tools">
          <Button onClick={ () => { applyOptions(); setIsFilterMenuOpen(false); } } loading={ loading }>
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Parts</span>
          <div className="page-header-options">
            <Button
              onClick={ () => {
                variables.service_part_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.service_part?.edges.map((entity) => ({
                      ID: entity.id,
                      "Part Number": entity.part_no || "",
                      Quantity: entity.quantity ?? "",
                      Source: partSourceType(entity),
                      "Source Detail": partSourceLabel(entity),
                      Crane: entity.crane?.name || "",
                      Tech: techName(entity.user_id),
                      Date: formatDate(entity.created_at),
                    }))
                  );
                  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `parts_${DateTime.now().toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS).replace(/:/g, "-").replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              } }
              loading={ csvLoading }
            >
              <FaFileCsv />
            </Button>
            <button onClick={ () => setIsFilterMenuOpen(true) }>
              <FaCog />
            </button>
          </div>
        </div>

        <ResponsiveTableView
          style={ { height: "100%" } }
          loading={ loading }
          headers={ [
            { label: "ID" },
            { label: "Part Number" },
            { label: "Qty" },
            { label: "Source" },
            { label: "Crane" },
            { label: "Tech" },
            { label: "Date" },
          ] }
          data={ list.map((entity) => ({
            id: entity.id,
            ID: entity.id,
            "Part Number": entity.part_no || "—",
            Qty: entity.quantity ?? "—",
            Source: partSourceType(entity),
            Crane: entity.crane?.name || "—",
            Tech: techName(entity.user_id),
            Date: formatDate(entity.created_at),
            selected: String(entity.id) === String(entityId),
          })) }
          identityColumnKey="ID"
          onRowClick={ (row) => props.onSelect(row.id, true) }
          totalCount={ count }
          addMoreResults={ () => setLimit(limit + 100) }
        />
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
